// 新增裝置 → 模擬 IoT 裝置（docs/iot-sim/03-sd.md D4；對齊 Android PhoneCamJoin.kt simulator=true、iOS PhoneCamJoinView simulator: true）。
// 產生 8 碼短碼 → 使用者在手機「LS IoT 模擬器」App 輸入 → 每 2 秒輪詢 → 比對碼確認 → 加入 → 等模擬器連上。
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../lib/api';
import {
  approveSimActivation,
  cancelSimBinding,
  createSimBinding,
  formatShortCode,
  getSimActivation,
  rejectSimActivation,
  simBindingStatus,
  simIotErrorKey,
  type SimActivation,
  type SimBinding,
} from './simIot';

interface Props {
  onClose?: () => void;
  onBound?: (deviceId: string) => void;
  /** 測試用：輪詢間隔（毫秒） */
  pollMs?: number;
}

type Step =
  | { kind: 'creating' }
  | { kind: 'code'; b: SimBinding }
  | { kind: 'review'; a: SimActivation; bindingId?: string }
  | { kind: 'busy' }
  | { kind: 'connecting'; bindingId?: string; deviceId?: string }
  | { kind: 'done'; deviceId: string }
  | { kind: 'expired' }
  | { kind: 'rejected' }
  | { kind: 'failed'; key: string };

function stepOf(a: SimActivation, bindingId?: string): Step {
  switch (a.state) {
    case 'awaiting_confirm':
      return { kind: 'review', a, bindingId };
    case 'approved':
    case 'issued':
      return { kind: 'connecting', bindingId: bindingId ?? a.binding_id, deviceId: a.device_id };
    case 'bound':
      return a.device_id ? { kind: 'done', deviceId: a.device_id } : { kind: 'expired' };
    case 'rejected':
      return { kind: 'rejected' };
    default:
      return { kind: 'expired' };
  }
}

export function SimIotJoinPanel({ onClose, onBound, pollMs = 2000 }: Props) {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>({ kind: 'creating' });
  const [run, setRun] = useState(0);
  const [now, setNow] = useState(() => Date.now() / 1000);
  const stepRef = useRef(step);
  stepRef.current = step;
  const onBoundRef = useRef(onBound);
  onBoundRef.current = onBound;

  const fail = useCallback((e: unknown): Step => {
    const key = simIotErrorKey(e);
    return key === 'simiot.expired' ? { kind: 'expired' } : { kind: 'failed', key };
  }, []);

  // 1. 建綁定碼
  useEffect(() => {
    let live = true;
    setStep({ kind: 'creating' });
    createSimBinding()
      .then((b) => { if (live) setStep({ kind: 'code', b }); })
      .catch((e) => { if (live) setStep(fail(e)); });
    return () => { live = false; };
  }, [run, fail]);

  // 2. 顯示短碼時輪詢：有人認領 → 等申請送出 → 確認畫面
  const codeBinding = step.kind === 'code' ? step.b : null;
  useEffect(() => {
    if (!codeBinding) return;
    let live = true;
    const tick = async () => {
      if (Date.now() / 1000 >= codeBinding.expires_at) { setStep({ kind: 'expired' }); return; }
      try {
        const st = await simBindingStatus(codeBinding.binding_id);
        if (!live) return;
        if (st.activation_id) {
          const a = await getSimActivation(st.activation_id);
          if (!live) return;
          if (a.state !== 'claimed') { setStep(stepOf(a, codeBinding.binding_id)); return; }
        }
        if (st.status === 'cancelled' || st.status === 'failed') { setStep({ kind: 'expired' }); return; }
      } catch (e) {
        if (!live) return;
        if (e instanceof ApiError) { setStep(fail(e)); return; }
        // 網路抖動：下一輪再試
      }
      if (live) timer = setTimeout(() => void tick(), pollMs);
    };
    let timer = setTimeout(() => void tick(), pollMs);
    return () => { live = false; clearTimeout(timer); };
  }, [codeBinding, pollMs, fail]);

  // 倒數
  useEffect(() => {
    if (!codeBinding) return;
    const id = setInterval(() => setNow(Date.now() / 1000), 1000);
    return () => clearInterval(id);
  }, [codeBinding]);

  // 3. 加入後等模擬器 bind（最多 3 分鐘；憑證已發仍算成功）
  const conn = step.kind === 'connecting' ? step : null;
  useEffect(() => {
    if (!conn) return;
    let live = true;
    const end = Date.now() + 3 * 60_000;
    const finish = (id: string) => { onBoundRef.current?.(id); setStep({ kind: 'done', deviceId: id }); };
    const tick = async () => {
      if (Date.now() >= end) {
        if (conn.deviceId) finish(conn.deviceId);
        else setStep({ kind: 'failed', key: 'simiot.errGeneric' });
        return;
      }
      try {
        if (conn.bindingId) {
          const st = await simBindingStatus(conn.bindingId);
          if (!live) return;
          const id = st.device_id ?? conn.deviceId;
          if (st.status === 'bound' && id) { finish(id); return; }
        } else if (conn.deviceId) {
          finish(conn.deviceId);
          return;
        }
      } catch { /* 下一輪 */ }
      if (live) timer = setTimeout(() => void tick(), pollMs);
    };
    let timer = setTimeout(() => void tick(), 0);
    return () => { live = false; clearTimeout(timer); };
  }, [conn, pollMs]);

  const approve = async (a: SimActivation, bindingId?: string) => {
    setStep({ kind: 'busy' });
    try {
      const r = await approveSimActivation(a.activation_id);
      setStep({ kind: 'connecting', bindingId: bindingId ?? r.binding_id, deviceId: r.device_id });
    } catch (e) {
      setStep(fail(e));
    }
  };

  const reject = async (a: SimActivation) => {
    setStep({ kind: 'busy' });
    try {
      await rejectSimActivation(a.activation_id);
      setStep({ kind: 'rejected' });
    } catch (e) {
      setStep(fail(e));
    }
  };

  const close = () => {
    // 短碼還沒人用就作廢（best effort），免得留著一組 30 分鐘有效的碼
    const s = stepRef.current;
    if (s.kind === 'code') void cancelSimBinding(s.b.binding_id).catch(() => undefined);
    onClose?.();
  };

  const left = codeBinding ? Math.max(0, Math.floor(codeBinding.expires_at - now)) : 0;

  return (
    <div className="card dv-add-panel" data-testid="simiot-join-panel">
      <div className="dv-pairing-head">
        <div>
          <h2>{t('simiot.title')}</h2>
          <p className="text-secondary">{t('simiot.intro')}</p>
        </div>
        {onClose && (
          <div className="dv-pairing-actions">
            <button className="btn-ghost" onClick={close}>{t('common.close')}</button>
          </div>
        )}
      </div>

      <section className="dv-pairing-summary" aria-live="polite">
        {(step.kind === 'creating' || step.kind === 'busy') && <p className="text-secondary">{t('simiot.creating')}</p>}

        {step.kind === 'code' && (
          <>
            <ol className="dv-simiot-steps">
              <li>{t('simiot.step1')}</li>
              <li>{t('simiot.step2')}</li>
              <li>{t('simiot.step3')}</li>
            </ol>
            <p className="dv-simiot-code" data-testid="simiot-short-code" aria-label={t('simiot.codeLabel')}>
              {formatShortCode(step.b.short_code)}
            </p>
            <p className="text-secondary" data-testid="simiot-waiting">
              {t('simiot.waiting')} · {t('simiot.expires', { m: Math.floor(left / 60), s: String(left % 60).padStart(2, '0') })}
            </p>
          </>
        )}

        {step.kind === 'review' && (
          <>
            <h3>{step.a.kind && step.a.kind !== 'sim-doorbell' ? t('simiot.reviewTitleGeneric') : t('simiot.reviewTitle')}</h3>
            {step.a.model_name && <p>{t('simiot.reviewModel', { model: step.a.model_name })}</p>}
            <p>{t('simiot.reviewMatch')}</p>
            <p className="dv-simiot-code" data-testid="simiot-match-code">{step.a.match_code}</p>
            <p className="text-secondary">{t('simiot.reviewWarn')}</p>
            <div className="dv-pairing-actions">
              <button className="btn-primary" onClick={() => void approve(step.a, step.bindingId)}>{t('simiot.approve')}</button>
              <button className="btn-ghost" onClick={() => void reject(step.a)}>{t('simiot.reject')}</button>
            </div>
          </>
        )}

        {step.kind === 'connecting' && <p className="text-secondary">{t('simiot.connecting')}</p>}

        {step.kind === 'done' && <h3 data-testid="simiot-done">{t('simiot.done')}</h3>}

        {(step.kind === 'expired' || step.kind === 'failed' || step.kind === 'rejected') && (
          <>
            {step.kind === 'rejected' ? (
              <p>{t('simiot.rejected')}</p>
            ) : (
              <p className="error-text" data-testid="simiot-error">{step.kind === 'expired' ? t('simiot.expired') : t(step.key)}</p>
            )}
            <div className="dv-pairing-actions">
              <button className="btn-primary" onClick={() => setRun((n) => n + 1)}>{t('simiot.regenerate')}</button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
